import {
  type HomeVisitFields,
  type SoapContentItem,
  type Soaps,
} from '@/shared/types/yakurekiSoap'

// ── 訪問報告書 フィールドモデルの単一 SSoT ────────────────────────────────
// 表示順・ラベル・PATCH で送るキー・soaps からの items 解決を全てここに集約する。
// パネル / 編集 hook / コピーの各所で個別定義するとキーずれで報告書が静かに壊れるため、
// 「報告書を構成するフィールドの知識」はこのファイルだけが持つ。

// 訪問時状況キーの表示順とラベル。back の home_care/services/visit_report.py の
// HOME_VISIT_SITUATION_KEYS と 1:1 対応 (順序も一致)。会話 + 薬剤師メモ + SOAP から LLM 生成
// (言及が無いキーは空で届き、薬剤師が訪問時確認で補完する)。
export const HOME_VISIT_SITUATION_LABELS: {
  key: keyof HomeVisitFields
  label: string
}[] = [
  { key: 'residual_drugs', label: '服薬・残薬状況' },
  { key: 'meal', label: '食事' },
  { key: 'excretion', label: '排泄' },
  { key: 'sleep', label: '睡眠' },
]

// 報告書の「配列フィールド」= 医師への伝達事項 (physician_message) + 訪問時状況。
// パネル描画 / PATCH で送るキー / itemsOf の対象は全てこの 1 リストから導出する。
// 伝達事項が先頭なのは提供先フォーマットの記入順に合わせるため。
export const HOME_VISIT_REPORT_FIELDS: { key: string; label: string }[] = [
  { key: 'physician_message', label: '医師への伝達事項' },
  ...HOME_VISIT_SITUATION_LABELS,
]

// soaps サイドカーから該当フィールドの items を解決する単一リゾルバ。
// physician_message は専用サイドカー、それ以外は home_visit_fields[key]。
export const itemsOfField = (
  soaps: Soaps | null | undefined,
  key: string
): SoapContentItem[] => {
  if (key === 'physician_message') return soaps?.physician_message ?? []
  const hv = soaps?.home_visit_fields as
    | Record<string, SoapContentItem[]>
    | null
    | undefined
  return hv?.[key] ?? []
}

// itemsOfField の書き込み対。該当キーだけを差し替えた新しい soaps を返す (非破壊)。
// 「どのキーがどのサイドカーに載るか」の知識を読み書きで割らないため、itemsOfField と
// 対でこのファイルに置く。楽観更新 (cache の setQueryData) から呼ぶ。
export const soapsWithField = (
  soaps: Soaps | null | undefined,
  key: string,
  items: SoapContentItem[]
): Soaps | null => {
  if (!soaps) return null
  if (key === 'physician_message') {
    return { ...soaps, physician_message: items }
  }
  return {
    ...soaps,
    home_visit_fields: {
      ...(soaps.home_visit_fields ?? {}),
      [key]: items,
    } as HomeVisitFields,
  }
}

export const itemsToText = (items: SoapContentItem[]): string =>
  items.map((item) => item.content).join('\n')

// チェック (is_selected) の付いた行だけを本文化する。コピーは採否を尊重する。
export const selectedText = (items: SoapContentItem[]): string =>
  items
    .filter((item) => item.is_selected ?? true)
    .map((item) => item.content)
    .join('\n')

// 訪問報告書 全体コピー用のテキストを組み立てる (純粋関数・テスト対象)。
// 各フィールド (記載があり採用された行のみ) を 【見出し】+ 本文 のブロックで空行区切り連結する。
// 空/全除外のフィールドはブロックごと出さない。
export const buildHomeVisitReportText = (soaps: Soaps | null): string => {
  const blocks: string[] = []
  HOME_VISIT_REPORT_FIELDS.forEach(({ key, label }) => {
    const text = selectedText(itemsOfField(soaps, key))
    if (text) blocks.push(`【${label}】\n${text}`)
  })
  return blocks.join('\n\n')
}
